import { FC, useEffect, useRef } from 'react';

import { useSession } from '@eneco/auth';
import { useApplication } from '@eneco/common/application';
import { useChat, Namespace, ChatArgs, Visibility } from '@eneco/components/chat/useChat';
import { OpenAPI } from '@eneco/dc/client/OpenAPI';
import { useCookieConsentDecision } from '@eneco/hooks/cookieConsent';

type ChatProps = {
  namespace: Namespace;
  fields?: {
    chatNamespace?: {
      id?: string;
      url?: string;
      name?: string;
      displayName?: string;
      fields?: {
        namespace?: {
          value?: Namespace;
        };
      };
    };
    chatProviderTopic?: {
      id?: string;
      url?: string;
      name?: string;
      displayName?: string;
      fields?: {
        namespace?: {
          value?: string;
        };
      };
    };
  };
};

const Chat: FC<ChatProps> = ({ namespace, fields }) => {
  const hasCookieConsentDecision = useCookieConsentDecision();
  const [mount] = useChat();
  const { searchParams } = useApplication();
  const topicToChat = searchParams.get('topicToChat');
  const textToChat = searchParams.get('textToChat');
  const stateToChat = searchParams.get('stateToChat') as Visibility;

  const { data: session, status } = useSession() ?? {};

  const namespaceJSS = fields?.chatNamespace?.fields?.namespace?.value;
  const topicJSS = fields?.chatProviderTopic?.fields?.namespace?.value;

  // Remembers the last set of arguments we dispatched to `mount`. This guards
  // against re-dispatching the same topic/message when the effect re-runs for a
  // reason unrelated to a user intent change — most notably next-auth refetching
  // the session when the browser tab regains focus, which changes the `session`
  // reference and would otherwise re-trigger setTopic/sendMessage and re-send an
  // activity to the bot. A genuine change (new deeplink params or remount on
  // navigation) produces a new signature and still dispatches.
  const lastDispatchRef = useRef<string | null>(null);

  useEffect(() => {
    return () => {
      mount({ visibility: 'hidden' });
    };
  }, [mount]);

  useEffect(() => {
    if (!hasCookieConsentDecision) {
      return;
    }

    if (status === 'loading') {
      return;
    }

    const token = session?.idToken ?? OpenAPI.TOKEN;

    const chatArgs: ChatArgs = {
      askText: textToChat ?? undefined,
      namespace: namespace ?? namespaceJSS,
      topic: topicToChat ?? topicJSS,
      context: textToChat
        ? {
            variables: {
              event: textToChat ? 'start_with_question' : undefined,
            },
          }
        : undefined,
      visibility: stateToChat ?? (textToChat || topicToChat ? 'open' : undefined),
    };

    const dispatchSignature = JSON.stringify({
      namespace: chatArgs.namespace,
      topic: chatArgs.topic,
      askText: chatArgs.askText,
      visibility: chatArgs.visibility,
    });

    // Skip if these exact arguments were already dispatched (e.g. the effect
    // re-ran only because the session reference changed on tab refocus).
    if (lastDispatchRef.current === dispatchSignature) {
      return;
    }
    lastDispatchRef.current = dispatchSignature;

    mount({
      ...chatArgs,
      api: {
        getConversationAuthToken: () => token,
      },
    });
  }, [
    hasCookieConsentDecision,
    mount,
    namespace,
    namespaceJSS,
    session,
    stateToChat,
    status,
    textToChat,
    topicJSS,
    topicToChat,
  ]);

  return null;
};

export default Chat;
